import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Pages.css';

const emptyForm = { practitioner_id: '', name: '', description: '', duration_minutes: 60, price: '', visibility_status: 'visible', recordable: false };

function AdminServices({ apiUrl, token }) {
  const [services, setServices] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = () => {
    axios.get(`${apiUrl}/admin/services`, { headers }).then((res) => setServices(res.data)).catch((err) => setError(err.response?.data?.error || 'Failed to load services'));
    axios.get(`${apiUrl}/admin/users?role=practitioner`, { headers }).then((res) => setPractitioners(res.data)).catch(() => {});
  };

  useEffect(fetchAll, [apiUrl, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setFormData({
      practitioner_id: s.practitioner_id || '',
      name: s.name,
      description: s.description || '',
      duration_minutes: s.duration_minutes || 60,
      price: s.price,
      visibility_status: s.visibility_status,
      recordable: s.recordable === true
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await axios.patch(`${apiUrl}/admin/services/${editingId}`, formData, { headers });
      } else {
        await axios.post(`${apiUrl}/admin/services`, formData, { headers });
      }
      setShowForm(false);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save service');
    }
  };

  const handleDelete = async (id) => {
    setError('');
    try {
      await axios.delete(`${apiUrl}/admin/services/${id}`, { headers });
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete service');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Manage Services</h1>
        <button className="btn btn-primary" onClick={openCreate}>+ New Service</button>
      </div>
      {error && <div className="error-message">{error}</div>}

      <div className="list-container table-wrap">
        <table className="table">
          <thead>
            <tr><th>Service</th><th>Practitioner</th><th>Price</th><th>Recordable</th><th>Visibility</th><th></th></tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td>{s.practitioner_name || <span className="text-muted">Unassigned</span>}</td>
                <td>${parseFloat(s.price).toFixed(2)}</td>
                <td>{s.recordable ? <span className="badge badge-info">Recordable</span> : <span className="text-muted">—</span>}</td>
                <td><span className={`badge badge-${s.visibility_status === 'hidden' ? 'inactive' : 'active'}`}>{s.visibility_status}</span></td>
                <td>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(s)} style={{ marginRight: 6 }}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {services.length === 0 && <tr><td colSpan="6" className="empty-state">No services yet</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit Service' : 'New Service'}</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Practitioner</label>
                <select value={formData.practitioner_id} onChange={(e) => setFormData({ ...formData, practitioner_id: e.target.value })} required>
                  <option value="">Select a practitioner...</option>
                  {practitioners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Service Name</label>
                <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea rows="3" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Duration (minutes)</label>
                  <input type="number" value={formData.duration_minutes} onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Price ($)</label>
                  <input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label>Visibility</label>
                <select value={formData.visibility_status} onChange={(e) => setFormData({ ...formData, visibility_status: e.target.value })}>
                  <option value="visible">Visible</option>
                  <option value="hidden">Hidden</option>
                  <option value="special_pricing">Special Pricing</option>
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  checked={formData.recordable}
                  onChange={(e) => setFormData({ ...formData, recordable: e.target.checked })}
                />
                Allow clients to opt in to having this session recorded
              </label>
              <button type="submit" className="btn btn-primary btn-block">{editingId ? 'Save Changes' : 'Create Service'}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminServices;
